import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChangelogEntry } from '@/lib/changelog/changelog-types';
import {
  CHANGELOG_FALLBACK_SOURCES,
  CHANGELOG_PRODUCT_ORDER,
  type ChangelogProductId,
  type ChangelogSource,
} from '@/lib/changelog/changelog-sources';
import * as changelogService from '@/services/changelog.service';

type SourcesMap = Record<ChangelogProductId, ChangelogSource>;

type CacheState = {
  sources: SourcesMap;
  loading: boolean;
  error: string | null;
  /** Incrementado a cada fetch; listeners ignoram respostas stale. */
  generation: number;
};

const listeners = new Set<() => void>();

let cache: CacheState = {
  sources: CHANGELOG_FALLBACK_SOURCES,
  loading: false,
  error: null,
  generation: 0,
};

let inflight: Promise<void> | null = null;

function emptyShellSources(): SourcesMap {
  return {
    xat: { ...CHANGELOG_FALLBACK_SOURCES.xat, entries: [] },
    ajuda: { ...CHANGELOG_FALLBACK_SOURCES.ajuda, entries: [] },
  };
}

function notify() {
  for (const listener of listeners) listener();
}

function setCache(patch: Partial<CacheState>) {
  cache = { ...cache, ...patch };
  notify();
}

async function fetchSourcesFromApi(): Promise<SourcesMap> {
  const products = await changelogService.getPublicProducts();
  const next = emptyShellSources();
  const productErrors: string[] = [];

  const loaded = await Promise.all(
    products.map(async (product) => {
      const id = product.slug as ChangelogProductId;
      if (id !== 'xat' && id !== 'ajuda') return null;
      let versions: ChangelogEntry[] = [];
      try {
        versions = await changelogService.getPublicVersions(product.slug);
      } catch (err) {
        productErrors.push(product.slug);
        console.warn(`[changelog] falha ao carregar versões de ${product.slug}`, err);
        versions = [];
      }
      return {
        id,
        source: {
          id,
          label: product.label,
          shortLabel: product.shortLabel,
          heroTitle: product.heroTitle,
          heroDescription: product.heroDescription,
          entries: versions,
        } satisfies ChangelogSource,
      };
    })
  );

  for (const row of loaded) {
    if (!row) continue;
    next[row.id] = row.source;
  }

  for (const id of CHANGELOG_PRODUCT_ORDER) {
    if (next[id].entries.length === 0) {
      next[id] = {
        ...next[id],
        entries: CHANGELOG_FALLBACK_SOURCES[id].entries,
      };
    }
  }

  if (productErrors.length > 0) {
    console.warn(
      `[changelog] fallback estático aplicado para: ${productErrors.join(', ')}`
    );
  }

  return next;
}

async function ensureLoaded(force = false): Promise<void> {
  if (!force && !cache.loading && cache.generation > 0 && inflight === null) {
    return;
  }
  if (inflight && !force) {
    await inflight;
    return;
  }

  const generation = cache.generation + 1;
  setCache({ loading: true, error: null, generation });

  const request = (async () => {
    try {
      const next = await fetchSourcesFromApi();
      if (generation !== cache.generation) return;
      setCache({ sources: next, loading: false, error: null });
    } catch (err) {
      console.warn('[changelog] sync com o servidor falhou', err);
      if (generation !== cache.generation) return;
      setCache({
        sources: CHANGELOG_FALLBACK_SOURCES,
        loading: false,
        error: 'Não foi possível sincronizar o changelog com o servidor. A mostrar dados locais.',
      });
    }
  })();

  inflight = request;
  try {
    await request;
  } finally {
    if (inflight === request) inflight = null;
  }
}

export function useChangelogSources() {
  const [snapshot, setSnapshot] = useState<CacheState>(() => cache);

  useEffect(() => {
    const onChange = () => setSnapshot({ ...cache });
    listeners.add(onChange);
    onChange();
    void ensureLoaded(false);
    return () => {
      listeners.delete(onChange);
    };
  }, []);

  const reload = useCallback(async () => {
    await ensureLoaded(true);
  }, []);

  const productOrder = useMemo(() => CHANGELOG_PRODUCT_ORDER, []);

  return {
    sources: snapshot.sources,
    loading: snapshot.loading,
    error: snapshot.error,
    productOrder,
    reload,
  };
}
